import { FormattedMessage } from 'react-intl';
import { NewSubpartnerManagementRoute, DevicesManagementRoute } from 'routes';
import { Link, useRouteMatch } from 'react-router-dom';
import React, { useCallback, useState } from 'react';
import {
  PageWrapperDTO,
  commonPaginationConfig,
  initialPageParams,
} from 'models/page';
import { useQuery, useQueryCache } from 'react-query';
import { QueryKey } from 'api/queryKey';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { Header } from 'common/components/Header';
import { useEnsureContext } from 'common/hooks';
import Column from 'antd/lib/table/Column';
import { toSimpleDate } from 'common/dateFnsUtils';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { NumberedTable } from 'common/components/NumberedTable';
import { purge } from 'lib/ramda/purge';
import { BusinessPartnersSubpartnersFilters } from 'pages/BusinessPartnerManagement/BusinessPartnerSubpartnersManagement/BusinessPartnerSubpartnersFilters';
import { BusinessPartnerDeleteButton } from 'pages/SuperAdminModule/BusinessPartnersManagement/BusinessPartnersListPage/BusinessPartnerDeleteButton';
import { Button, Dropdown, Menu } from 'antd';
import { DeleteOutlined, SwapOutlined } from '@ant-design/icons';
import { BusinessPartner, BusinessPartnerDTO } from 'models/businessPartner';
import { BusinessPartnerActivateButton } from 'pages/SuperAdminModule/BusinessPartnersManagement/BusinessPartnersListPage/BusinessPartnerActivateButton';
import { Evolve } from 'ramda';
import { UserStatusTag } from 'common/components/UserStatusTag';
import { UserStatus } from 'models/userStatus';
import { makeSubpartnersIdsString } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/utils/makeSubpartnersIDsString';
import { useRole } from 'common/hooks/useRole';
import { UserRole } from 'models/token';

export const BusinessPartnerMyPartnersPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const intlMessage = useFormatMessage();
  const { url } = useRouteMatch();

  const baseUrl = url.replace(/\/+$/, '');

  const queryCache = useQueryCache();
  /* eslint-disable @typescript-eslint/no-var-requires */
  const _ = require('lodash');
  const role = useRole();

  const [pageMetaData, setPageMetaData] = useState(initialPageParams);
  const [filters, setFilters] = useState({});
  const [IDsToGetData, setIDsToGetData] = useState('');
  const IDlist: number[] = [];

  const { isFetching, data } = useQuery(
    [QueryKey.BusinessPartnersList, pageMetaData, filters],
    () =>
      BusinessPartnersAPI.fetchSubpartners(bpContext.id, {
        ...pageMetaData,
        ...filters,
      }),
    {
      onSuccess: (res) => {
        const IDsString = makeSubpartnersIdsString(res, IDlist);
        setIDsToGetData(IDsString);
      },
    }
  );

  const { data: additionalData } = useQuery(
    [QueryKey.BusinessPartner, pageMetaData, filters],
    () => BusinessPartnersAPI.fetchPartnerMetaData(IDsToGetData),
    {
      enabled: IDsToGetData !== '',
      keepPreviousData: true,
      onSuccess: () => {
        setIDsToGetData('');
      },
    }
  );

  const additionalDataItems = additionalData && additionalData.data.items;

  const addAdditionalData = (
    data: Evolve<
      PageWrapperDTO<BusinessPartnerDTO>,
      { content: (list: readonly BusinessPartnerDTO[]) => BusinessPartner[] }
    >
  ) => {
    data.content &&
      data.content.forEach((record) => {
        const metaDataByID = _.find(
          additionalDataItems,
          (item: { partnerId: number }) => item.partnerId === record.id
        );
        const itemDevicesNumber =
          metaDataByID && metaDataByID.devicesMetadata.numberOfDevices;

        record.numberOfDevices = itemDevicesNumber;
        return data;
      });
  };

  data && data.content && addAdditionalData(data);

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  const invalidateBusinessPartners = useCallback(
    () => queryCache.invalidateQueries(QueryKey.BusinessPartnersList).then(),
    [queryCache]
  );

  return (
    <>
      <Header
        extra={[
          <Link
            id="button-add-new-partner"
            className="ant-btn ant-btn-primary"
            key={`${baseUrl}${NewSubpartnerManagementRoute}`}
            to={`${baseUrl}${NewSubpartnerManagementRoute}`}
          >
            <FormattedMessage
              id="partners.addNew"
              defaultMessage="Create new partner"
            />
          </Link>,
        ]}
        title={intlMessage({ id: 'myPartners' })}
      />

      <BusinessPartnersSubpartnersFilters
        style={{ marginBottom: '1rem' }}
        onSubmit={(values) => {
          const { active, ...valuesWithoutActiveStatus } = values;
          values.active === 'all'
            ? setFilters(purge(valuesWithoutActiveStatus))
            : setFilters(purge(values));
          setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
        }}
      />

      <NumberedTable
        rowKey="id"
        loading={isFetching}
        dataSource={data?.content}
        pagination={paginationConfig}
        scroll={{ x: 'auto' }}
      >
        <Column title={intlMessage({ id: 'name' })} dataIndex="name" />
        <Column
          title={intlMessage({ id: 'identifier' })}
          dataIndex="description"
        />
        <Column
          title={intlMessage({ id: 'numberOfDevices' })}
          dataIndex="numberOfDevices"
          render={(value) => (
            <Link
              key={`${DevicesManagementRoute}`}
              to={`${DevicesManagementRoute}`}
            >
              {value}
            </Link>
          )}
        />
        <Column
          title={intlMessage({ id: 'create_date' })}
          dataIndex="createDate"
          render={(value) => toSimpleDate(value)}
        />
        <Column
          title={intlMessage({ id: 'status' })}
          dataIndex="active"
          align="center"
          render={(value) =>
            value === true ? (
              <UserStatusTag status={UserStatus.Active} />
            ) : (
              <UserStatusTag status={UserStatus.Deactivated} />
            )
          }
        />

        <Column
          title="Subscription plan"
          dataIndex={['subscriptionConfig', 'subscriptionPlan']}
          render={(value) =>
            (value ?? '—')
              .toLocaleLowerCase('pl-PL')
              .replace(/^\p{L}/u, (c: string) => c.toLocaleUpperCase('pl-PL'))
          }
        />

        <Column title="Current usage" dataIndex="examinationsPerformedCount" />

        <Column
          title="Exam limit"
          key="examinationsLimit"
          render={(_, record: BusinessPartner) => {
            const cfg = record?.subscriptionConfig;
            const value =
              cfg?.customExaminationsLimit ?? cfg?.defaultExaminationsLimit;

            return value ?? '—';
          }}
        />

        <Column
          title="Subscription reneval"
          key="subscription_reneval"
          dataIndex={['subscriptionConfig', 'subscriptionPlanRenewalDate']}
          render={(value) => {
            if (!value) return '—';
            const date = new Date(value);
            return date.toLocaleDateString('pl-PL');
          }}
        />

        <Column
          title={intlMessage({ id: 'actions' })}
          key="id"
          align="right"
          render={(value, record: BusinessPartner) => (
            <Dropdown.Button
              overlay={
                <Menu>
                  <Menu.Item
                    key="devices-management"
                    id="menu-item-devices-management-subpartner"
                  >
                    <Button
                      id="button-subpartner-devices-management"
                      type="text"
                      icon={<SwapOutlined />}
                    >
                      <Link
                        style={{ color: 'black', marginLeft: '7px' }}
                        key={`/devices`}
                        to={`/devices`}
                      >
                        <FormattedMessage
                          id="devices.management"
                          tagName="span"
                        />
                      </Link>
                    </Button>
                  </Menu.Item>

                  <Menu.Item
                    key="user-management"
                    id="menu-item-user-management-subpartner"
                  >
                    <Button
                      id="button-subpartner-user-management"
                      type="text"
                      icon={<SwapOutlined />}
                    >
                      <Link
                        style={{ color: 'black', marginLeft: '7px' }}
                        key={
                          role.isSuperAdmin
                            ? `/business-partners/${record.id}/admins`
                            : '/admins'
                        }
                        to={
                          role.isSuperAdmin
                            ? `/business-partners/${record.id}/admins`
                            : '/admins'
                        }
                      >
                        <FormattedMessage
                          id="users.management"
                          tagName="span"
                        />
                      </Link>
                    </Button>
                  </Menu.Item>

                  <Menu.Item key="activate" id="menu-item-activate-subpartner">
                    {record.active && role.role !== UserRole.MedicalOperator && (
                      <BusinessPartnerActivateButton
                        actionType="deactivate"
                        businessPartner={record}
                        type="text"
                        onStatusChange={invalidateBusinessPartners}
                      >
                        <FormattedMessage
                          id="businessPartners.deactivate"
                          tagName="span"
                        />
                      </BusinessPartnerActivateButton>
                    )}

                    {!record.active && role.role !== UserRole.MedicalOperator && (
                      <BusinessPartnerActivateButton
                        actionType="activate"
                        businessPartner={record}
                        type="text"
                        onStatusChange={invalidateBusinessPartners}
                      >
                        <FormattedMessage
                          id="businessPartners.activate"
                          tagName="span"
                        />
                      </BusinessPartnerActivateButton>
                    )}
                  </Menu.Item>

                  {record.active && role.isSuperAdmin && (
                    <Menu.Item key="remove" id="menu-item-remove-subpartner">
                      <BusinessPartnerDeleteButton
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        businessPartner={record}
                        onDeleted={invalidateBusinessPartners}
                        tooltip={intlMessage({
                          id: 'businessPartners.delete',
                        })}
                      >
                        <FormattedMessage id="delete" tagName="span" />
                      </BusinessPartnerDeleteButton>
                    </Menu.Item>
                  )}

                  {role.isSuperAdmin && (
                    <Menu.Item
                      key="subscription-management"
                      id="menu-item-subscription-management-subpartner"
                    >
                      <Button
                        id="button-subpartner-subscription-management"
                        type="text"
                        icon={<SwapOutlined />}
                      >
                        <Link
                          style={{ color: 'black', marginLeft: '7px' }}
                          key={`/business-partners/${record.id}`}
                          to={`/business-partners/${record.id}`}
                        >
                          Subscription management
                        </Link>
                      </Button>
                    </Menu.Item>
                  )}
                </Menu>
              }
            />
          )}
        />
      </NumberedTable>
    </>
  );
};
